import type {
  ClientQuestionnaireDTO,
  GetClientQuestionnaireResponse,
  SaveClientQuestionnaireRequest,
  SaveClientQuestionnaireResponse,
  SubmitClientQuestionnaireRequest,
  SubmitClientQuestionnaireResponse,
} from '@brainkey/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { clientQuestionnairesApi } from '../api/endpoints';
import { queryKeys } from '../queryClient';

export function useClientQuestionnaires(): UseQueryResult<ClientQuestionnaireDTO[]> {
  return useQuery({
    queryKey: queryKeys.clientQuestionnaires.list,
    queryFn: async ({ signal }) => (await clientQuestionnairesApi.list(signal)).questionnaires,
  });
}

export function useClientQuestionnaire(id: string): UseQueryResult<GetClientQuestionnaireResponse> {
  return useQuery({
    queryKey: queryKeys.clientQuestionnaires.detail(id),
    queryFn: ({ signal }) => clientQuestionnairesApi.get(id, signal),
    enabled: id !== '',
  });
}

/** Autosave. No polling counterpart is needed here — unlike report
 * generation, a save/submit response is the authoritative post-action
 * state, not a trigger for an async job to poll for. */
export function useSaveClientQuestionnaire(
  id: string,
): UseMutationResult<SaveClientQuestionnaireResponse, unknown, SaveClientQuestionnaireRequest> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveClientQuestionnaireRequest) => clientQuestionnairesApi.save(id, body),
    onSuccess: (data) => {
      // Merge into whatever's cached rather than overwriting `structure` —
      // the detail GET is the only source of the assessment catalogue, and
      // save/submit responses don't repeat it.
      qc.setQueryData<GetClientQuestionnaireResponse>(
        queryKeys.clientQuestionnaires.detail(id),
        (old) => ({
          questionnaire: data.questionnaire,
          structure: old?.structure ?? { kind: null },
        }),
      );
    },
  });
}

export function useSubmitClientQuestionnaire(
  id: string,
): UseMutationResult<SubmitClientQuestionnaireResponse, unknown, SubmitClientQuestionnaireRequest> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitClientQuestionnaireRequest) =>
      clientQuestionnairesApi.submit(id, body),
    onSuccess: (data) => {
      qc.setQueryData<GetClientQuestionnaireResponse>(
        queryKeys.clientQuestionnaires.detail(id),
        (old) => ({
          questionnaire: data.questionnaire,
          structure: old?.structure ?? { kind: null },
        }),
      );
      void qc.invalidateQueries({ queryKey: queryKeys.clientQuestionnaires.list });
    },
  });
}
